import {localized, msg} from '@lit/localize';
import {customElement, property, state} from 'lit/decorators.js';
import {html, type HTMLTemplateResult, LitElement} from 'lit';
import './components/element-input.ts';
import './components/element-button.ts';
import './components/element-poi-image.ts';
import {classMap} from 'lit/directives/class-map.js';
import type {InternalPOIDetails, TourPrefs} from '../apis/api-tours.js';
import {poiPointSvg} from '../helpers/elements.ts';
import {
  type ImageValidity,
  type UploadedEvent,
} from './components/element-poi-image.ts';
import type {POIMeta} from '../stores/store-main.ts';

@localized()
@customElement('element-poi-edit')
export default class ElementPoiEdit extends LitElement {
  @property({type: Object}) pref: TourPrefs = undefined;
  @state() private poiTitle: string;
  @state() private poiDescription: string;
  @state() private poiPhoto: string;
  @state() private poiPhotoValidity: ImageValidity;
  @state() private poiIndex: number;

  @property({type: Object})
  set poi(poi: InternalPOIDetails) {
    this.poiTitle = poi.title || '';
    this.poiDescription = poi.description || '';
    this.poiPhoto = poi.photo;
    this.poiPhotoValidity = poi.photoValidity;
    this.poiIndex = poi.index;
  }

  override render(): HTMLTemplateResult {
    const preventConfirm = this.poiTitle.length < 1;
    return html`
      <div class="overflow-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-y-[12px] gap-x-[16px]">
          <div class="grow flex flex-row h-full">
            <div class="w-[26px]">
              ${poiPointSvg({
                classes: 'w-[19px] h-[33px] mr-2',
                fill: this.pref.colorRgb,
                number: this.poiIndex + 1,
              })}
            </div>
            <div
              class="flex flex-col  gap-y-[12px] col-span-full md:col-span-1 w-full"
            >
              <element-input
                .required="${true}"
                placeholder="${msg('Name')}"
                .value="${this.poiTitle}"
                @input="${(evt: InputEvent) => {
                  this.poiTitle = (<HTMLInputElement>evt.target).value;
                }}"
              >
              </element-input>
              <element-input
                class="overflow-auto flex-auto"
                .textArea="${true}"
                .availableHeight="${true}"
                placeholder="${msg('Description')}"
                .value="${this.poiDescription}"
                @input="${(evt: InputEvent) =>
                  (this.poiDescription = (<HTMLInputElement>evt.target).value)}"
              >
              </element-input>
            </div>
          </div>
          <div class="col-span-full md:col-span-1 pl-[26px] md:pl-0">
            <element-poi-image
              class="col-[1_/_span_4] xxl:col-[3_/_span_4] flex"
              hint="${msg('Drag or click to add photo, max. 10 MB')}"
              .photo="${this.poiPhoto}"
              .validity="${this.poiPhotoValidity}"
              @uploaded="${(evt: UploadedEvent) => {
                this.poiPhoto = evt.detail.photo;
                this.poiPhotoValidity = 'temp';
              }}"
              @deleted="${() => {
                this.poiPhoto = undefined;
                this.poiPhotoValidity = undefined;
              }}"
            >
            </element-poi-image>
          </div>
        </div>
      </div>
      <div
        class="col-span-full mt-[40px] flex justify-end gap-x-[16px] pl-[26px]"
      >
        <element-button
          .type=${'secondary'}
          .label="${() => msg('Cancel')}"
          @click="${this.cancel}"
        ></element-button>
        <div class="${classMap({'cursor-not-allowed': preventConfirm})}">
          <element-button
            .disabled=${preventConfirm}
            .label="${() => msg('Confirm')}"
            @click="${this.confirm}"
          ></element-button>
        </div>
      </div>
    `;
  }

  cancel(_e: Event) {
    this.dispatchEvent(new CustomEvent<void>('cancelEditPoi'));
  }

  confirm(_e: Event) {
    const meta: POIMeta = {
      title: this.poiTitle,
      description: this.poiDescription,
      photo: this.poiPhoto,
      photoValidity: this.poiPhotoValidity,
    };
    this.dispatchEvent(
      new CustomEvent('confirmEditPoi', {
        detail: meta,
      }),
    );
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-poi-edit': ElementPoiEdit;
  }
}
